import React, { useRef, useState, useEffect, useMemo } from 'react';
import cloneDeep from 'lodash.clonedeep';
import { FaCheck, FaDownload, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { RiFileExcel2Line, RiSparklingFill, RiFileUploadLine } from 'react-icons/ri';
import { MdClose, MdWarning } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAzurePublicUpload } from '../../hooks/useAzurePublicUpload';
import { useTransporterData } from '../../hooks/useTransporterData';
import { useCreateTransporterProfile } from '../../hooks/useCreateTransporterProfile';
import { Urls } from '../../Urls';
import { downloadFileToLocal } from '../../shared_utilities/fileDownloadUtils';
import { Constants } from '../../Constants';
import axios from '../../Axios';
import obClasses from '../Onboarding/Onboarding.module.css';
import classes from './ProfileUpdateModal.module.css';
import ControlledPopupModal from '../Shared/PopupModal/ControlledPopupModal/ControlledPopupModal';
import Dropdown from '../Shared/Forms/Dropdown/Dropdown';
import LocationAutoComplete from '../Shared/AutoCompleteV2/AutoCompleteV2';
import { GoCheckCircleFill } from "react-icons/go";
import { useOnboardingMixpanelTrack } from '../Onboarding/hooks/useOnboardingMixpanelTrack';

// ── AI extraction stages (same as OnboardingUpload) ───────────────────────────
const EXTRACT_STAGES = [
    'Uploading file to secure storage…',
    'Processing document pages…',
    'Extracting content with AI…',
    'Mapping fields to your profile…',
    'Almost there, finalising…',
];
const STAGE_DELAYS = [0, 4000, 10000, 18000, 26000];
const AI_ACCEPTED_TYPES = '.pdf,.png,.jpg,.jpeg,.xlsx,.xls';
const CONFIDENCE_THRESHOLD = 0.7;

// ── Field helpers (same as OnboardingReview) ──────────────────────────────────
const getVal = (fields, key) => {
    const f = fields[key];
    if (!f) return null;
    return (typeof f === 'object' && 'value' in f) ? f.value : f;
};
const getConf = (fields, key) => {
    const f = fields[key];
    if (!f || typeof f !== 'object') return null;
    return f.confidence ?? null;
};
const isLowConf = (fields, key) => { const c = getConf(fields, key); return c !== null && c < CONFIDENCE_THRESHOLD; };
const wasAiFilled = (fields, key) => {
    const v = getVal(fields, key);
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
};
const countAiFilled = (fields) => {
    let count = 0;
    Object.values(fields).forEach(f => {
        const v = typeof f === 'object' && 'value' in f ? f.value : f;
        if (v === null || v === undefined || v === '') return;
        if (Array.isArray(v)) { if (v.length > 0) count++; return; }
        if (typeof v === 'object') { if (Object.values(v).some(x => x !== null && x !== '')) count++; return; }
        if (String(v).trim()) count++;
    });
    return count;
};

// ── Inline sub-components (defined at module scope to avoid remount on keypress)
const AiInputClear = ({ show, onClear }) =>
    show ? (<button type="button" className={obClasses.aiClearBtn} onClick={onClear} title="Clear AI-filled value"><MdClose size={12} /></button>) : null;

const SectionToggle = ({ title, sKey, openSections, toggleSection, hasAiFilled, children }) => (
    <div className={obClasses.reviewSectionBlock}>
        <button className={obClasses.reviewSectionToggle} onClick={() => toggleSection(sKey)} type="button">
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                {title}
                {hasAiFilled && (
                    <span className={obClasses.aiSectionBadge}><RiSparklingFill size={8} /> AI filled</span>
                )}
            </span>
            {openSections[sKey] ? <FaChevronUp /> : <FaChevronDown />}
        </button>
        {openSections[sKey] && <div className={obClasses.reviewSectionContent}>{children}</div>}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const ProfileUpdateModal = ({ show, onClose }) => {
    const [view, setView] = useState('picker');
    const [extractedFields, setExtractedFields] = useState(null);

    const { trackOnboardingEvent } = useOnboardingMixpanelTrack();
    const { data: transporterInfo } = useTransporterData();

    useEffect(() => {
        if (show) {
            setView('picker');
            setExtractedFields(null);
            trackOnboardingEvent(
                'profile update',
                'Profile update modal opened',
                { transporter_id: transporterInfo?.transporter_id }
            );
        }
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        trackOnboardingEvent(
            'profile update',
            'Profile update modal closed',
            { transporter_id: transporterInfo?.transporter_id, view_when_closed: view }
        );
        onClose();
    };

    const getTitle = () => {
        if (view === 'picker') return 'Update Your Profile';
        if (view === 'docExtract' || view === 'docReview') return 'Auto-fill with AI';
        return 'Excel Template';
    };
    const getSubtitle = () => {
        if (view === 'picker') return 'Choose how you would like to bulk-update your profile data';
        if (view === 'docExtract') return 'Upload a document and let AI extract your profile fields instantly';
        if (view === 'docReview') return 'Review and confirm the extracted fields before saving';
        return 'Download your pre-filled template, update it offline, then upload';
    };

    return (
        <ControlledPopupModal
            isOpen={show}
            onClose={handleClose}
            title={getTitle()}
            subtitle={getSubtitle()}
            size="lg"
            overflowStyles={{ marginTop: '3%' }}
            backdrop={true}
            closeButtonNeeded={true}
            closeButtonId="update-modal-close"
            content={
                <div className={classes.modalContent}>
                    {view === 'picker' && <PickerView onChoose={setView} transporterInfo={transporterInfo} trackOnboardingEvent={trackOnboardingEvent} />}
                    {view === 'docExtract' && (
                        <DocExtractView
                            onBack={() => {
                                trackOnboardingEvent(
                                    'profile update ai upload',
                                    'Navigated back to method picker',
                                    { transporter_id: transporterInfo?.transporter_id }
                                );
                                setView('picker');
                            }}
                            onExtracted={fields => { setExtractedFields(fields); setView('docReview'); }}
                        />
                    )}
                    {view === 'docReview' && (
                        <DocReviewView
                            fields={extractedFields || {}}
                            onBack={() => setView('docExtract')}
                            onClose={handleClose}
                        />
                    )}
                    {view === 'excel' && <ExcelView onBack={() => {
                        trackOnboardingEvent(
                            'profile update excel',
                            'Navigated back to method picker from Excel view',
                            { transporter_id: transporterInfo?.transporter_id }
                        );
                        setView('picker');
                    }} onClose={handleClose} />}
                </div>
            }
        />
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase 1: Method Picker
// ─────────────────────────────────────────────────────────────────────────────
const PickerView = ({ onChoose, transporterInfo, trackOnboardingEvent }) => (
    <div className={classes.pickerWrap}>
        <div className={classes.methodCards}>
            {/* AI Card — visually dominant */}
            <div className={`${classes.methodCard} ${classes.methodCardAi}`} onClick={() => {
                trackOnboardingEvent(
                    'profile update picker',
                    'AI extraction method selected',
                    { transporter_id: transporterInfo?.transporter_id }
                );
                onChoose('docExtract');
            }} id="update-picker-ai">
                <div className={classes.methodCardBadge}>AI Powered ✦</div>
                <div className={classes.methodCardIcon}><RiSparklingFill /></div>
                <div className={classes.methodCardTitle}>Auto-fill with AI</div>
                <div className={classes.methodCardDesc}>
                    Upload any company document — brochure, profile PDF, or image — and our AI extracts and fills your profile fields instantly.
                </div>
                <button className={`${classes.methodCardBtn} ${classes.methodCardBtnAi}`} type="button">
                    Use AI Extraction →
                </button>
            </div>

            {/* Excel Card */}
            <div className={`${classes.methodCard} ${classes.methodCardExcel}`} onClick={() => {
                trackOnboardingEvent(
                    'profile update picker',
                    'Excel template method selected',
                    { transporter_id: transporterInfo?.transporter_id }
                );
                onChoose('excel');
            }} id="update-picker-excel">
                <div className={classes.methodCardIcon} style={{ color: '#1d6f42' }}><RiFileExcel2Line /></div>
                <div className={classes.methodCardTitle}>Excel Template</div>
                <div className={classes.methodCardDesc}>
                    Download your pre-filled profile as an Excel file, update fields offline at your pace, then upload it back.
                </div>
                <button className={`${classes.methodCardBtn} ${classes.methodCardBtnExcel}`} type="button">
                    Use Excel →
                </button>
            </div>
        </div>
        <p className={classes.pickerFootnote}>
            You can also edit individual fields manually from any profile page.
        </p>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Phase 2a: Upload (extract only — no DB write here)
// ─────────────────────────────────────────────────────────────────────────────
const DocExtractView = ({ onBack, onExtracted }) => {
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [stageIdx, setStageIdx] = useState(0);
    const [error, setError] = useState('');
    const inputRef = useRef();

    const { data: transporterData } = useTransporterData();
    const { uploadFile } = useAzurePublicUpload();
    const { trackOnboardingEvent } = useOnboardingMixpanelTrack();

    useEffect(() => {
        if (!extracting) { setStageIdx(0); return; }
        const timers = STAGE_DELAYS.slice(1).map((delay, i) => setTimeout(() => setStageIdx(i + 1), delay));
        return () => timers.forEach(clearTimeout);
    }, [extracting]);

    const onDragOver = e => { e.preventDefault(); setDragging(true); };
    const onDragLeave = () => setDragging(false);
    const onDrop = e => {
        e.preventDefault(); setDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) {
            setError('');
            setFile(f);
            trackOnboardingEvent(
                'profile update ai upload',
                'Document selected for AI extraction',
                { file_name: f.name, file_type: f.name.split('.').pop().toLowerCase() }
            );
        }
    };
    const removeFile = () => {
        trackOnboardingEvent(
            'profile update ai upload',
            'Document removed',
            { file_name: file?.name }
        );
        setFile(null); setError('');
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleExtract = async () => {
        if (!file) { setError('Please select a file first.'); return; }
        setExtracting(true); setError('');
        trackOnboardingEvent(
            'profile update ai upload',
            'AI extraction started for profile update',
            { file_name: file.name, transporter_id: transporterData?.transporter_id }
        );
        try {
            const customFilename = `${transporterData?.transporter_id}/${file.name}`;
            const { blobUrl } = await uploadFile(file, customFilename);
            const formData = new FormData();
            formData.append('file_url', blobUrl);
            const response = await axios.post(Urls.profileFileUpload, formData);
            const data = response.data;
            // API returns fields object — pass to review step
            const fields = data?.fields ?? (typeof data === 'object' ? data : {});
            const aiCount = Object.values(fields).filter(f => {
                const v = typeof f === 'object' && 'value' in f ? f.value : f;
                return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length);
            }).length;
            trackOnboardingEvent(
                'profile update ai upload',
                'AI extraction completed — Review opened',
                { transporter_id: transporterData?.transporter_id, ai_filled_count: aiCount }
            );
            onExtracted(fields);
        } catch (err) {
            const errMsg = err?.response?.data?.detail || 'Failed to process the document. Please try again.';
            trackOnboardingEvent(
                'profile update ai upload',
                'AI extraction failed for profile update',
                { error: errMsg, transporter_id: transporterData?.transporter_id }
            );
            setError(errMsg);
        } finally {
            setExtracting(false);
        }
    };

    return (
        <div className={classes.viewWrap}>
            <div className={classes.aiHint}>
                <RiSparklingFill className={classes.aiHintIcon} />
                <span>Upload any document — PDF, Word, image, or Excel. AI will extract relevant fields for your review before anything is saved.</span>
            </div>

            {extracting ? (
                <div className={obClasses.extractingWrap}>
                    <div className={obClasses.extractingSpinner} />
                    <div className={obClasses.extractingText} style={{ marginBottom: 16 }}>Analysing your document…</div>
                    <div className={obClasses.extractStages}>
                        {EXTRACT_STAGES.map((label, i) => {
                            const isDone = i < stageIdx, isActive = i === stageIdx;
                            return (
                                <div key={i} className={`${obClasses.extractStageRow} ${isActive ? obClasses.extractStageActive : ''} ${isDone ? obClasses.extractStageDone : ''}`}>
                                    <div className={`${obClasses.extractStageDot}${isDone ? ` ${obClasses.extractStageDotDone}` : ''}`}>
                                        {isDone ? <FaCheck size={8} /> : isActive ? <span className={obClasses.extractStagePulse} /> : null}
                                    </div>
                                    <span>{label}</span>
                                </div>
                            );
                        })}
                    </div>
                    <p style={{ fontSize: 11, color: '#b0aed4', marginTop: 14 }}>Please don't refresh the page.</p>
                </div>
            ) : (
                <>
                    <div
                        className={`${obClasses.dropZone} ${dragging ? obClasses.dragging : ''}`}
                        onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
                        onClick={() => !file && inputRef.current?.click()}
                        id="update-doc-drop-zone"
                        style={{ minHeight: 160 }}
                    >
                        <input ref={inputRef} type="file" accept={AI_ACCEPTED_TYPES}
                            style={{ display: 'none' }}
                            onChange={e => e.target.files[0] && (() => {
                                setError('');
                                setFile(e.target.files[0]);
                                trackOnboardingEvent(
                                    'profile update ai upload',
                                    'Document selected for AI extraction',
                                    { file_name: e.target.files[0].name, file_type: e.target.files[0].name.split('.').pop().toLowerCase() }
                                );
                            })()} />
                        {!file ? (
                            <>
                                <div className={obClasses.dropIcon}><RiFileUploadLine /></div>
                                <div className={obClasses.dropTitle}>Drag & drop your file here</div>
                                <div className={obClasses.dropSub}>or</div>
                                <span className={obClasses.browseLink}>Browse files</span>
                                <div className={obClasses.uploadFormats}>PDF · XLSX · PNG · JPG</div>
                            </>
                        ) : (
                            <div className={obClasses.fileChosen} onClick={e => e.stopPropagation()}>
                                <RiFileUploadLine className={obClasses.fileChosenIcon} />
                                <div className={obClasses.fileChosenName}>{file.name}</div>
                                <span className={obClasses.removeFile} onClick={removeFile} title="Remove"><IoCloseCircleOutline /></span>
                            </div>
                        )}
                    </div>
                    {error && <div className={obClasses.uploadError}>{error}</div>}
                    <div className={classes.viewFooter}>
                        <button className={obClasses.btnSecondary} onClick={onBack} id="update-doc-back">← Back</button>
                        <button className={obClasses.btnPrimary} onClick={handleExtract} disabled={!file} id="update-doc-submit">
                            Extract Fields →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase 2b: Review extracted fields + save to profile
// ─────────────────────────────────────────────────────────────────────────────
const DocReviewView = ({ fields, onBack, onClose }) => {
    const { data: transporterData, refetch } = useTransporterData();
    const { createProfile, saving, error: saveErr } = useCreateTransporterProfile();
    const { trackOnboardingEvent } = useOnboardingMixpanelTrack();

    const aiFilledCount = useMemo(() => countAiFilled(fields), []); // eslint-disable-line

    // Derived constants
    const fyOptions = cloneDeep(Constants.FinancialYears || []);
    const defaultFy = fyOptions[1]?.value ?? '2024-2025';
    const zoneOptions = Array.isArray(Constants?.AreaOfOperation) ? cloneDeep(Constants.AreaOfOperation) : [];
    const transportationModes = Constants.TransportationModes || [];
    const companyTypeOptions = cloneDeep(Constants.CompanyTypes || []);

    // ── Editable state from extracted fields ───────────────────────────────
    const [companyName, setCompanyName] = useState(getVal(fields, 'transporter_name') || transporterData?.transporter_name || '');
    const [headquarter, setHeadquarter] = useState(getVal(fields, 'headquarter') || transporterData?.headquarter || '');
    const [companyType, setCompanyType] = useState(() => {
        const raw = getVal(fields, 'company_type') || transporterData?.company_type || '';
        const opts = companyTypeOptions;
        return opts.find(o => o.value.toLowerCase() === raw.toLowerCase())?.value || raw;
    });
    const [zone, setZone] = useState(() => {
        const raw = getVal(fields, 'zone') || getVal(fields, 'area_of_operation') || transporterData?.zone || [];
        const arr = Array.isArray(raw) ? raw : [String(raw)];
        return arr.map(z => {
            const opt = zoneOptions.find(o => o.value.toLowerCase() === z.toLowerCase() || o.label?.toLowerCase() === z.toLowerCase());
            return opt ? opt.value : null;
        }).filter(Boolean);
    });
    const [selectedModes, setSelectedModes] = useState(() => {
        const extracted = getVal(fields, 'modes_of_transportation') || transporterData?.modes_of_transportation || [];
        return (Array.isArray(extracted) ? extracted : []).map(m => m?.name).filter(n => transportationModes.some(tm => tm.name === n));
    });
    const [website, setWebsite] = useState(String(getVal(fields, 'website') || transporterData?.website || ''));
    const [aboutUs, setAboutUs] = useState(String(getVal(fields, 'about_us') || transporterData?.about_us || ''));
    const [hqAddress, setHqAddress] = useState(String(getVal(fields, 'hq_address') || transporterData?.hq_address || ''));
    const [panNumber, setPanNumber] = useState(String(getVal(fields, 'pan_number') || transporterData?.pan_number || ''));
    const [cinNumber, setCinNumber] = useState(String(getVal(fields, 'cin_number') || transporterData?.cin_number || ''));
    const [established, setEstablished] = useState(String(getVal(fields, 'established') || transporterData?.established || ''));
    const [experienceYear, setExperienceYear] = useState(String(getVal(fields, 'experience_year') || transporterData?.experience_year || ''));
    const [lspEmail, setLspEmail] = useState(String(getVal(fields, 'lsp_email') || transporterData?.lsp_email || ''));
    const [domainBusiness, setDomainBusiness] = useState(() => {
        const raw = getVal(fields, 'domain_business') || transporterData?.domain_business || [];
        return Array.isArray(raw) ? raw.join(', ') : String(raw);
    });
    const rawOwned = getVal(fields, 'owned_fleet') || transporterData?.owned_fleet || {};
    const [ownedFleet, setOwnedFleet] = useState(typeof rawOwned === 'object' ? (rawOwned.amount || '') : String(rawOwned));
    const rawAssoc = getVal(fields, 'associated_fleet') || transporterData?.associated_fleet || {};
    const [associatedFleet, setAssociatedFleet] = useState(typeof rawAssoc === 'object' ? (rawAssoc.amount || '') : String(rawAssoc));
    const rawTurnover = getVal(fields, 'turnover') || transporterData?.turnover || [];
    const [turnover, setTurnover] = useState(
        Array.isArray(rawTurnover) && rawTurnover.length
            ? rawTurnover.slice(0, 3).map(t => ({ financial_year: t.financial_year || defaultFy, amount: t.amount || '', unit: t.unit || 'CR' }))
            : [{ financial_year: defaultFy, amount: '', unit: 'CR' }]
    );
    const rawNetProfit = getVal(fields, 'net_profit') || transporterData?.net_profit || [];
    const [netProfit, setNetProfit] = useState(
        Array.isArray(rawNetProfit) && rawNetProfit.length
            ? rawNetProfit.slice(0, 2).map(t => ({ financial_year: t.financial_year || defaultFy, amount: t.amount || '', unit: t.unit || 'CR' }))
            : [{ financial_year: defaultFy, amount: '', unit: 'CR' }]
    );

    const [openSections, setOpenSections] = useState({ essential: true, company: false, operations: false, fleet: false, financials: false });
    const [errors, setErrors] = useState({});
    const toggleSection = key => {
        const willOpen = !openSections[key];
        setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
        trackOnboardingEvent(
            'profile update ai review',
            'Review section toggled',
            { section_key: key, opened: willOpen }
        );
    };

    // Low-confidence fields
    const mandatoryKeys = ['transporter_name', 'headquarter', 'zone', 'modes_of_transportation'];
    const lowCount = mandatoryKeys.filter(k => isLowConf(fields, k)).length;

    // Track review screen mount
    useEffect(() => {
        trackOnboardingEvent(
            'profile update ai review',
            'AI review screen opened for profile update',
            { ai_filled_count: aiFilledCount, low_conf_count: lowCount }
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // AI fill badges
    const aiCompany = ['website', 'lsp_email', 'established', 'experience_year', 'hq_address', 'about_us', 'domain_business'].some(k => wasAiFilled(fields, k));
    const aiFleet = ['owned_fleet', 'associated_fleet'].some(k => wasAiFilled(fields, k));
    const aiFinancials = ['turnover', 'net_profit'].some(k => wasAiFilled(fields, k));

    const toggleMode = name => setSelectedModes(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);

    const buildPayload = () => {
        const base = cloneDeep(transporterData || {});
        base.transporter_name = companyName.trim();
        base.headquarter = headquarter.trim();
        base.hq_address = hqAddress.trim();
        base.zone = zone;
        base.company_type = companyType;
        base.website = website.trim();
        base.lsp_email = lspEmail.trim();
        base.established = established.trim();
        base.experience_year = experienceYear;
        base.about_us = aboutUs.trim();
        base.pan_number = panNumber.trim();
        base.cin_number = cinNumber.trim();
        base.domain_business = domainBusiness ? domainBusiness.split(',').map(s => s.trim()).filter(Boolean) : [];
        base.modes_of_transportation = transportationModes.filter(tm => selectedModes.includes(tm.name)).map(tm => ({ name: tm.name, values: [] }));
        if (ownedFleet) base.owned_fleet = { amount: ownedFleet };
        if (associatedFleet) base.associated_fleet = { amount: associatedFleet };
        const filledTurnover = turnover.filter(t => t.amount);
        if (filledTurnover.length) base.turnover = filledTurnover.map(t => ({ ...t, currency: ['Thousands', 'Millions'].includes(t.unit) ? '$' : '₹' }));
        const filledNetProfit = netProfit.filter(t => t.amount);
        if (filledNetProfit.length) base.net_profit = filledNetProfit.map(t => ({ ...t, currency: ['Thousands', 'Millions'].includes(t.unit) ? '$' : '₹' }));
        return base;
    };

    const handleSave = async () => {
        const errs = {};
        if (!companyName.trim()) errs.companyName = 'Required';
        setErrors(errs);
        if (Object.keys(errs).length) return;

        const payload = buildPayload();
        trackOnboardingEvent(
            'profile update ai review',
            'Profile update saved via AI extraction',
            { transporter_id: transporterData?.transporter_id, fields_count: aiFilledCount }
        );
        const { success } = await createProfile(payload);
        if (success) {
            trackOnboardingEvent(
                'profile update ai review',
                'Profile updated successfully via AI',
                { transporter_id: transporterData?.transporter_id }
            );
            await refetch();
            toast.success('Profile updated successfully!');
            onClose();
        } else {
            trackOnboardingEvent(
                'profile update ai review',
                'Profile update failed via AI',
                { error: saveErr, transporter_id: transporterData?.transporter_id }
            );
        }
    };

    const FinRow = ({ label, entry, setEntry }) => (
        <div className={obClasses.finRow} style={{ marginBottom: 6 }}>
            <div className={obClasses.finLabel} style={{ flex: '0 0 70px', fontSize: 12 }}>{label}</div>
            <div className={obClasses.finFields}>
                <div style={{ flex: '0 0 110px' }}>
                    <select className={obClasses.finSelect} value={entry.financial_year} onChange={e => setEntry(prev => ({ ...prev, financial_year: e.target.value }))}>
                        {fyOptions.map(fy => <option key={fy.value} value={fy.value}>{fy.label}</option>)}
                    </select>
                </div>
                <input className={`${obClasses.textInput} ${obClasses.finAmtInput}`} type="number" placeholder="Amount" value={entry.amount} min="0" onChange={e => setEntry(prev => ({ ...prev, amount: e.target.value }))} />
                <select className={obClasses.finUnitSelect} value={entry.unit} onChange={e => setEntry(prev => ({ ...prev, unit: e.target.value }))}>
                    <option value="CR">₹ CR</option>
                    <option value="Lakhs">₹ Lakhs</option>
                    <option value="Thousands">$ Thousands</option>
                    <option value="Millions">$ Millions</option>
                </select>
            </div>
        </div>
    );

    return (
        <div className={classes.viewWrap}>
            {/* AI summary bar */}
            <div className={classes.reviewSummaryBar}>
                <div className={classes.reviewSummaryAi}>
                    <RiSparklingFill style={{ color: '#393185', marginRight: 5 }} />
                    AI pre-filled <strong>{aiFilledCount}</strong> fields
                </div>
                {lowCount > 0 && (
                    <div className={classes.reviewSummaryWarn}>
                        <MdWarning style={{ marginRight: 4 }} />
                        {lowCount} field{lowCount > 1 ? 's' : ''} need verification
                    </div>
                )}
                <div className={classes.reviewSummaryNote}>Review and edit before saving.</div>
            </div>

            <div className={classes.reviewScrollArea}>
                {/* Essential fields */}
                <SectionToggle title="Essential Info" sKey="essential" openSections={openSections} toggleSection={toggleSection} hasAiFilled={wasAiFilled(fields, 'transporter_name') || wasAiFilled(fields, 'headquarter')}>
                    <div className={obClasses.fieldGrid}>
                        <div className={`${obClasses.fieldWrap} ${obClasses.fieldFull}`}>
                            <div className={obClasses.fieldLabel}>Company Name</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput} ${errors.companyName ? obClasses.invalid : ''}${wasAiFilled(fields, 'transporter_name') && companyName ? ` ${obClasses.aiClearInput}` : ''}`}
                                    value={companyName} onChange={e => { setCompanyName(e.target.value); setErrors(p => ({ ...p, companyName: '' })); }} placeholder="Your company name" />
                                <AiInputClear show={wasAiFilled(fields, 'transporter_name') && !!companyName} onClear={() => setCompanyName('')} />
                            </div>
                            {errors.companyName && <div className={obClasses.errorMsg}>{errors.companyName}</div>}
                        </div>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>HQ / Registered Office</div>
                            <LocationAutoComplete
                                value={headquarter ? { label: headquarter, value: headquarter } : null}
                                onChange={sel => setHeadquarter(sel ? sel.value : '')}
                                onClear={() => setHeadquarter('')}
                                placeholder="Search city"
                            />
                        </div>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>Company Type</div>
                            <Dropdown picklistOptions={companyTypeOptions} defaultValues={companyTypeOptions.filter(o => o.value === companyType)} onValueChange={sel => setCompanyType(sel?.value || '')} placeholder="Select type" isSearchable />
                        </div>
                        <div className={`${obClasses.fieldWrap} ${obClasses.fieldFull}`}>
                            <div className={obClasses.fieldLabel}>Area of Operation</div>
                            <Dropdown picklistOptions={zoneOptions} defaultValues={zoneOptions.filter(o => zone.includes(o.value))} onValueChange={sel => setZone(Array.isArray(sel) ? sel.map(s => s?.value) : (sel && Object.keys(sel).length ? [sel.value] : []))} placeholder="Select regions" isMultiselect isSearchable isNewOptionValid />
                        </div>
                        <div className={`${obClasses.fieldWrap} ${obClasses.fieldFull}`}>
                            <div className={obClasses.fieldLabel}>Modes of Transportation</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {transportationModes.map(tm => {
                                    const sel = selectedModes.includes(tm.name);
                                    return (
                                        <span key={tm.name} onClick={() => toggleMode(tm.name)} style={{
                                            padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
                                            border: `1px solid ${sel ? '#393185' : '#dddaf0'}`, background: sel ? '#393185' : '#faf9fe', color: sel ? '#fff' : '#3d3a55',
                                        }}>{tm.name}</span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </SectionToggle>

                {/* Company info */}
                <SectionToggle title="Company Information" sKey="company" openSections={openSections} toggleSection={toggleSection} hasAiFilled={aiCompany}>
                    <div className={obClasses.fieldGrid}>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>Website</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'website') && website ? ` ${obClasses.aiClearInput}` : ''}`} value={website} onChange={e => setWebsite(e.target.value)} placeholder="www.yourcompany.com" />
                                <AiInputClear show={wasAiFilled(fields, 'website') && !!website} onClear={() => setWebsite('')} />
                            </div>
                        </div>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>Company Email</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'lsp_email') && lspEmail ? ` ${obClasses.aiClearInput}` : ''}`} type="email" value={lspEmail} onChange={e => setLspEmail(e.target.value)} placeholder="info@yourcompany.com" />
                                <AiInputClear show={wasAiFilled(fields, 'lsp_email') && !!lspEmail} onClear={() => setLspEmail('')} />
                            </div>
                        </div>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>Established Year</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'established') && established ? ` ${obClasses.aiClearInput}` : ''}`} value={established} onChange={e => setEstablished(e.target.value)} placeholder="e.g. 2005" />
                                <AiInputClear show={wasAiFilled(fields, 'established') && !!established} onClear={() => setEstablished('')} />
                            </div>
                        </div>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>Years of Experience</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'experience_year') && experienceYear ? ` ${obClasses.aiClearInput}` : ''}`} type="number" value={experienceYear} onChange={e => setExperienceYear(e.target.value)} placeholder="e.g. 20" />
                                <AiInputClear show={wasAiFilled(fields, 'experience_year') && !!experienceYear} onClear={() => setExperienceYear('')} />
                            </div>
                        </div>
                        <div className={`${obClasses.fieldWrap} ${obClasses.fieldFull}`}>
                            <div className={obClasses.fieldLabel}>HQ Full Address</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'hq_address') && hqAddress ? ` ${obClasses.aiClearInput}` : ''}`} value={hqAddress} onChange={e => setHqAddress(e.target.value)} placeholder="Full registered address" />
                                <AiInputClear show={wasAiFilled(fields, 'hq_address') && !!hqAddress} onClear={() => setHqAddress('')} />
                            </div>
                        </div>
                        <div className={`${obClasses.fieldWrap} ${obClasses.fieldFull}`}>
                            <div className={obClasses.fieldLabel}>About Us</div>
                            <div className={obClasses.aiFieldWrap} style={{ alignItems: 'flex-start' }}>
                                <textarea className={`${obClasses.textInput}${wasAiFilled(fields, 'about_us') && aboutUs ? ` ${obClasses.aiClearInput}` : ''}`} value={aboutUs} onChange={e => setAboutUs(e.target.value)} rows={3} style={{ resize: 'vertical' }} placeholder="Brief company description" />
                                <AiInputClear show={wasAiFilled(fields, 'about_us') && !!aboutUs} onClear={() => setAboutUs('')} />
                            </div>
                        </div>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>PAN Number</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'pan_number') && panNumber ? ` ${obClasses.aiClearInput}` : ''}`} value={panNumber} onChange={e => setPanNumber(e.target.value)} placeholder="AAAAA0000A" />
                                <AiInputClear show={wasAiFilled(fields, 'pan_number') && !!panNumber} onClear={() => setPanNumber('')} />
                            </div>
                        </div>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>CIN Number</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'cin_number') && cinNumber ? ` ${obClasses.aiClearInput}` : ''}`} value={cinNumber} onChange={e => setCinNumber(e.target.value)} placeholder="U12345AB2000PLC000000" />
                                <AiInputClear show={wasAiFilled(fields, 'cin_number') && !!cinNumber} onClear={() => setCinNumber('')} />
                            </div>
                        </div>
                        <div className={`${obClasses.fieldWrap} ${obClasses.fieldFull}`}>
                            <div className={obClasses.fieldLabel}>Business Domains (comma-separated)</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'domain_business') && domainBusiness ? ` ${obClasses.aiClearInput}` : ''}`} value={domainBusiness} onChange={e => setDomainBusiness(e.target.value)} placeholder="e.g. Primary Transport, Warehousing" />
                                <AiInputClear show={wasAiFilled(fields, 'domain_business') && !!domainBusiness} onClear={() => setDomainBusiness('')} />
                            </div>
                        </div>
                    </div>
                </SectionToggle>

                {/* Fleet */}
                <SectionToggle title="Fleet Strength" sKey="fleet" openSections={openSections} toggleSection={toggleSection} hasAiFilled={aiFleet}>
                    <div className={obClasses.fieldGrid}>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>Owned Fleet (count)</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'owned_fleet') && ownedFleet ? ` ${obClasses.aiClearInput}` : ''}`} value={ownedFleet} onChange={e => setOwnedFleet(e.target.value)} placeholder="e.g. 540" />
                                <AiInputClear show={wasAiFilled(fields, 'owned_fleet') && !!ownedFleet} onClear={() => setOwnedFleet('')} />
                            </div>
                        </div>
                        <div className={obClasses.fieldWrap}>
                            <div className={obClasses.fieldLabel}>Associated Fleet (count)</div>
                            <div className={obClasses.aiFieldWrap}>
                                <input className={`${obClasses.textInput}${wasAiFilled(fields, 'associated_fleet') && associatedFleet ? ` ${obClasses.aiClearInput}` : ''}`} value={associatedFleet} onChange={e => setAssociatedFleet(e.target.value)} placeholder="e.g. 200" />
                                <AiInputClear show={wasAiFilled(fields, 'associated_fleet') && !!associatedFleet} onClear={() => setAssociatedFleet('')} />
                            </div>
                        </div>
                    </div>
                </SectionToggle>

                {/* Financials */}
                <SectionToggle title="Financials" sKey="financials" openSections={openSections} toggleSection={toggleSection} hasAiFilled={aiFinancials}>
                    <div className={obClasses.reviewSubLabel} style={{ marginBottom: 6 }}>Turnover</div>
                    {turnover.map((entry, i) => (
                        <FinRow key={i} label={`FY ${i + 1}`} entry={entry} setEntry={upd => setTurnover(prev => prev.map((t, idx) => idx === i ? (typeof upd === 'function' ? upd(t) : upd) : t))} />
                    ))}
                    <div className={obClasses.reviewSubLabel} style={{ marginBottom: 6, marginTop: 10 }}>Net Profit</div>
                    {netProfit.map((entry, i) => (
                        <FinRow key={i} label={`FY ${i + 1}`} entry={entry} setEntry={upd => setNetProfit(prev => prev.map((t, idx) => idx === i ? (typeof upd === 'function' ? upd(t) : upd) : t))} />
                    ))}
                </SectionToggle>
            </div>

            {saveErr && <div className={obClasses.uploadError} style={{ marginTop: 8 }}>{saveErr}</div>}

            <div className={classes.viewFooter}>
                <button className={obClasses.btnSecondary} onClick={onBack} id="update-review-back" disabled={saving}>← Re-upload</button>
                <button className={obClasses.btnPrimary} onClick={handleSave} disabled={saving} id="update-review-save">
                    {saving ? 'Saving…' : 'Save to Profile →'}
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase 3: Excel Template
// ─────────────────────────────────────────────────────────────────────────────
const ExcelView = ({ onBack, onClose }) => {
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [downloadingPrefilled, setDownloadingPrefilled] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const inputRef = useRef();

    const { data: transporterData, refetch } = useTransporterData();
    const { uploadFile } = useAzurePublicUpload();
    const { trackOnboardingEvent } = useOnboardingMixpanelTrack();

    const onDragOver = e => { e.preventDefault(); setDragging(true); };
    const onDragLeave = () => setDragging(false);
    const onDrop = e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); };
    const handleFile = f => {
        setError('');
        const ext = f.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(ext)) { setError('Please upload an Excel file (.xlsx or .xls).'); return; }
        setFile(f);
        trackOnboardingEvent(
            'profile update excel',
            'Excel file selected for profile update',
            { file_name: f.name }
        );
    };
    const removeFile = () => {
        trackOnboardingEvent(
            'profile update excel',
            'Excel file removed',
            { file_name: file?.name }
        );
        setFile(null); setError('');
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleDownloadPrefilled = async () => {
        setDownloadingPrefilled(true);
        trackOnboardingEvent(
            'profile update excel',
            'Pre-filled Excel template downloaded',
            { transporter_id: transporterData?.transporter_id }
        );
        try {
            const res = await axios.get(Urls.excelPrefilled, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `transporter_profile_${transporterData?.transporter_id || 'me'}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            setError('Could not download pre-filled template. Try the blank template instead.');
        } finally {
            setDownloadingPrefilled(false);
        }
    };

    const handleDownloadBlank = () => {
        trackOnboardingEvent(
            'profile update excel',
            'Blank Excel template downloaded',
            { transporter_id: transporterData?.transporter_id }
        );
        downloadFileToLocal(Urls.excelTemplate);
    };

    const handleUpload = async () => {
        if (!file) { setError('Please select a filled Excel file first.'); return; }
        setUploading(true); setError('');
        trackOnboardingEvent(
            'profile update excel',
            'Excel import started for profile update',
            { file_name: file.name, transporter_id: transporterData?.transporter_id }
        );
        try {
            const customFilename = `${transporterData?.transporter_id || 'transporter'}/excel/${file.name}`;
            const { blobUrl } = await uploadFile(file, customFilename);
            const res = await axios.post(Urls.excelUpload, { blob_url: blobUrl });
            const data = res.data;
            if (data?.message !== 'Success') {
                const errMsg = data?.message || 'Upload completed but no data returned.';
                trackOnboardingEvent(
                    'profile update excel',
                    'Profile update via Excel failed',
                    { error: errMsg, transporter_id: transporterData?.transporter_id }
                );
                setError(errMsg);
                return;
            }
            trackOnboardingEvent(
                'profile update excel',
                'Profile updated successfully via Excel',
                {
                    transporter_id: transporterData?.transporter_id,
                    fields_updated_count: (data.fields_updated || []).length,
                    percentage_completion: data.percentage_completion,
                }
            );
            await refetch();
            setSuccessData({
                fields: data.fields_updated || [],
                percentage: data.percentage_completion || '',
                message: 'Profile updated successfully'
            });
        } catch (err) {
            const errMsg = err?.response?.data?.message || err?.response?.data?.detail || 'Failed to process the Excel file. Please try again.';
            trackOnboardingEvent(
                'profile update excel',
                'Profile update via Excel failed',
                { error: errMsg, transporter_id: transporterData?.transporter_id }
            );
            setError(errMsg);
        } finally {
            setUploading(false);
        }
    };

    if (successData) {
        return (
            <div className={classes.successWrap}>
                <div className={classes.successIcon}>
                    <GoCheckCircleFill size={60} color='#1d6f42'/>
                </div>

                <div className={classes.successTitle}>
                    {successData.message}
                </div>

                <div className={classes.successSubtitle}>
                    {successData.percentage && (
                        <span>{successData.percentage}% profile completed</span>
                    )}
                </div>

                {successData.fields.length > 0 && (
                    <div className={classes.successFields}>
                        <div className={classes.successFieldsTitle}>
                            Fields Updated:
                        </div>

                        <div className={classes.successFieldList}>
                            {successData.fields.map((f, i) => (
                                <span key={i} className={classes.successFieldChip}>
                                    {f.replaceAll('_', ' ')}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <button
                    className={obClasses.btnPrimary}
                    onClick={() => {
                        trackOnboardingEvent(
                            'profile update excel',
                            'User closed modal after successful Excel update',
                            { transporter_id: transporterData?.transporter_id }
                        );
                        onClose();
                    }}
                    style={{ marginTop: 16 }}
                >
                    Done
                </button>
            </div>
        );
    }

    return (
        <div className={classes.viewWrap}>
            <div className={classes.excelDownloadSection}>
                <div className={classes.excelDownloadSectionLabel}>Step 1 — Download your template</div>
                <div className={classes.excelDownloadButtons}>
                    <button className={`${classes.downloadBtn} ${classes.downloadBtnPrimary}`} onClick={handleDownloadPrefilled} disabled={downloadingPrefilled || uploading} id="update-excel-download-prefilled" type="button">
                        <FaDownload style={{ marginRight: 7 }} />
                        {downloadingPrefilled ? 'Downloading…' : 'Download Pre-filled Template'}
                    </button>
                    <button className={`${classes.downloadBtn} ${classes.downloadBtnOutlined}`} onClick={handleDownloadBlank} disabled={uploading} id="update-excel-download-blank" type="button">
                        <FaDownload style={{ marginRight: 7 }} />
                        Download Blank
                    </button>
                </div>
                <p className={classes.excelDownloadHint}>The pre-filled template already contains your existing profile data — just update the fields you want to change.</p>
            </div>

            <div className={classes.uploadDivider}><span>Step 2 — Upload your filled Excel</span></div>

            {uploading ? (
                <div className={obClasses.extractingWrap}>
                    <div className={obClasses.extractingSpinner} />
                    <div className={obClasses.extractingText}>Processing your Excel…</div>
                    <p style={{ fontSize: 11, color: '#b0aed4', marginTop: 10 }}>Please don't refresh the page.</p>
                </div>
            ) : (
                <>
                    <div
                        className={`${obClasses.dropZone} ${dragging ? obClasses.dragging : ''}`}
                        onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
                        onClick={() => !file && inputRef.current?.click()}
                        id="update-excel-drop-zone" style={{ minHeight: 120 }}
                    >
                        <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
                        {!file ? (
                            <>
                                <div className={obClasses.dropIcon} style={{ color: '#1d6f42' }}><RiFileExcel2Line /></div>
                                <div className={obClasses.dropTitle}>Drag & drop your filled Excel here</div>
                                <div className={obClasses.dropSub}>or</div>
                                <span className={obClasses.browseLink}>Browse files</span>
                                <div className={obClasses.uploadFormats}>XLSX · XLS</div>
                            </>
                        ) : (
                            <div className={obClasses.fileChosen} onClick={e => e.stopPropagation()}>
                                <RiFileExcel2Line className={obClasses.fileChosenIcon} style={{ color: '#1d6f42' }} />
                                <div className={obClasses.fileChosenName}>{file.name}</div>
                                <span className={obClasses.removeFile} onClick={removeFile} title="Remove"><IoCloseCircleOutline /></span>
                            </div>
                        )}
                    </div>
                    {error && <div className={obClasses.uploadError}>{error}</div>}
                    <div className={classes.viewFooter}>
                        <button className={obClasses.btnSecondary} onClick={onBack} id="update-excel-back">← Back</button>
                        <button className={obClasses.btnPrimary} onClick={handleUpload} disabled={!file || uploading} id="update-excel-submit" style={{ background: '#1d6f42' }}>
                            Import & Update →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ProfileUpdateModal;
